import useAuth from "containers/auth/useHooks";
import useForms from "hooks/useForms";
import useStore from "hooks/useStore";
import { Host_APIS } from "libs/apis/host.api";
import { MutableRefObject } from "react";
import { useNavigate } from "react-router-dom";
import { successToaster } from "utils/helpers/common/alert-service";
import { siteRoutes } from "utils/helpers/enums/routes";
import { EVENT_STATUS } from "utils/helpers/enums/shared.enum";
import { ResourceFilterDTO } from "utils/helpers/models/attendee/resource-filter.dto";
import { BadgeDTO } from "utils/helpers/models/host-dtos/badge.dto";
import { CheckoutSessionDTO } from "utils/helpers/models/host-dtos/checkout-session.dto";
import { PostJoineeDTO } from "utils/helpers/models/host-dtos/postReviewJoinee.dto";
import { SubscriptionDTO } from "utils/helpers/models/host-dtos/subscription.dto";
import { CommunityDTO } from "utils/helpers/models/shared/community.dto";
import {
  EventDTO,
  CreateEventPayloadDTO,
} from "utils/helpers/models/shared/event.dto";
import { FilterDTO } from "utils/helpers/models/shared/filter.dto";
import { MessageDTO } from "utils/helpers/models/shared/message.dto";
import { ResourceDTO } from "utils/helpers/models/shared/resource.dto";
import { RsvpDTO } from "utils/helpers/models/shared/rsvp.dto";

const useHost = () => {
  const navigate = useNavigate();
  const { getUserProfile } = useAuth();
  const { getUserId, updateRegisteredData } = useStore();
  const { patchFormValues } = useForms();
  const getSubscriptionPlans = async (setData: Function) => {
    const response = await Host_APIS.getSubscriptionPlans();
    const { status = false, data = [] } = response || {};
    if (status) {
      setData(
        data
          .map((i) => new SubscriptionDTO(i))
          .sort((a, b) => a.price - b.price)
      );
    }
  };

  const createCheckoutSession = async (body: CheckoutSessionDTO) => {
    const response = await Host_APIS.createCheckoutSession(body);
    const { status = false, data = {}, message = "" } = response || {};
    if (status) {
      successToaster(message);
      const {
        session: { url },
      } = data;
      // window.open(url);
      window.location.href = url;
    }
  };

  const getEvents = async (
    setData: Function,
    queryParams: any = {},
    previousData: EventDTO[] = [],
    setFilters?: Function
  ) => {
    const response = await Host_APIS.getEventsTestingRefactored(queryParams);
    const { status = false, data = {} } = response || {};
    if (status) {
      // Extract pagination info - handle both number and string types
      const totalPages = typeof data.totalPages === 'string' ? parseInt(data.totalPages, 10) : (data.totalPages || 0);
      const totalElements = typeof data.totalElements === 'string' ? parseInt(data.totalElements, 10) : (data.totalElements || 0);
      
      // Update filters with pagination info
      if (setFilters) {
        setFilters((filters: any) => ({ 
          ...filters, 
          totalPages: totalPages, 
          totalElements: totalElements 
        }));
      }
      
      // Map events and set data - replace data when filtering, append when loading more
      const events = Array.isArray(data.content) 
        ? data.content.map((i: any) => new EventDTO(i))
        : [];
      
      // If previousData is empty (filtering/searching), replace data; otherwise append (for pagination)
      setData(previousData.length === 0 ? events : [...previousData, ...events]);
    }
  };

  const getEventById = async (
    id: number,
    setFormData: Function,
    setValue?: Function,
    isDraft: boolean = false
  ) => {
    const response = await Host_APIS.getEventById(id);
    const { status = false, data = null } = response || {};
    if (status) {
      const { event, host, rsvp = {} } = data;
      const model = new EventDTO({ ...event, host, rsvp });

      // If it's a duplicated event, clear dates and badge
      if (isDraft) {
        model.workshopSessions = model.workshopSessions.map((session) => ({
          ...session,
          startDateTime: "",
          endDateTime: "",
        }));
        model.badgeUrlId = 0;
        model.eventBadge = null;
      }

      if (setValue) {
        patchFormValues(model, setFormData, setValue);
      } else {
        setFormData({ ...model });
      }
    }
  };

  const deleteRsvp = async (id: number, setData: Function) => {
    const response = await Host_APIS.deleteRsvp(id);
    const { status = false } = response || {};
    if (status) {
      getRsvps(setData);
    }
  };

  const getRsvps = async (setData: Function) => {
    const response = await Host_APIS.getRsvps();
    const { status = false, data = {} } = response || {};
    if (status) {
      setData(
        Array.isArray(data?.content)
          ? data.content.map((i) => new RsvpDTO(i))
          : []
      );
    }
  };

  const createBadge = async (body: BadgeDTO) => {
    const response = await Host_APIS.createBadge(body);
    const { status = false } = response || {};
    if (status) {
      // navigate(siteRoutes.myBadges);
    }
  };

  const getBadges = async (
    setData: Function,
    queryParams: any = {},
    setPagination?: Function
  ) => {
    const response = await Host_APIS.getBadges(queryParams);
    const { status = false, data = [] } = response || {};
    if (status) {
      setData(Array.isArray(data) ? data.map((i) => new BadgeDTO(i)) : []);
    }
  };

  const getAwardedBadges = async (
    setData: Function,
    queryParams: any = {},
    setPagination?: Function
  ) => {
    const response = await Host_APIS.getAwardedBadges(queryParams);
    const { status = false, data = {} } = response || {};
    if (status) {
      const {
        badges = [],
        pageNumber = 0,
        pageSize = 10,
        totalElements = 0,
      } = data;
      setData(Array.isArray(badges) ? badges.map((i) => new BadgeDTO(i)) : []);
      if (setPagination) {
        setPagination({
          page: pageNumber,
          size: pageSize,
          totalElements: totalElements,
        });
      }
    }
  };

  const getAdminBadges = async (
    setData: Function,
    queryParams: any = {},
    setPagination?: Function
  ) => {
    const response = await Host_APIS.getAdminBadges(queryParams);
    const { status = false, data = [] } = response || {};
    if (status) {
      setData(Array.isArray(data) ? data.map((i) => new BadgeDTO(i)) : []);
    }
  };

  const createEvent = async (
    body: CreateEventPayloadDTO,
    state: EVENT_STATUS
  ) => {
    const response = await Host_APIS.createEvent(body, state);
    const { status = false, message = "Success" } = response || {};
    if (status) {
      successToaster(message);
      navigate(-1);
    }
  };

  const getHostHome = async (
    setUpcomingEvents: Function,
    setPastEvents: Function
  ) => {
    const response = await Host_APIS.getHostHome();
    const { status = false, data = {} } = response || {};
    if (status) {
      const { upcomingEvents = [], pastEvents = [] } = data;
      setUpcomingEvents(upcomingEvents.map((i) => new EventDTO(i)));
      setPastEvents(pastEvents.map((i) => new EventDTO(i)));
    }
  };

  const cancelEvent = async (eventId: number, body: any) => {
    const response = await Host_APIS.cancelEvent(eventId, body);
    const { status = false, message = "" } = response || {};
    if (status) {
      successToaster(message);
      navigate(-1);
    }
  };

  const duplicateEvent = async (eventId: number) => {
    const response = await Host_APIS.duplicateEvent(eventId);
    const { status = false, message = "", data = {} } = response || {};
    if (status) {
      const { event } = data || {};
      successToaster(message);
      navigate(`${siteRoutes.createExperience}?id=${event.id}&draft=true`);
    }
  };

  const getCommunities = async (setData: Function, queryParams: any = {}) => {
    const response = await Host_APIS.getCommunities(queryParams);
    const { status = false, data = {} } = response || {};
    if (status) {
      const { content = [] } = data;
      setData(content.map((i) => new CommunityDTO(i)));
    }
  };

  const sendMessageToCommunity = async (
    message: MessageDTO,
    messages: MessageDTO[],
    setMessages: Function
  ) => {
    const response = await Host_APIS.sendMessageToCommunity(message);
    const { status = false, data = {} } = response || {};
    if (status) {
      // Update the last message with server response data
      setMessages((prevMessages) => {
        const updatedMessages = [...prevMessages];
        const lastIndex = updatedMessages.length - 1;
        if (lastIndex >= 0) {
          updatedMessages[lastIndex] = new MessageDTO({
            ...data,
            sentByMe: true,
          });
        }
        return updatedMessages;
      });
    }
    return response;
  };

  const getCommunityMessages = async (
    queryParams: any,
    setData: Function,
    messages: MessageDTO[],
    setFilters: Function,
    preventScrolling: Function
  ) => {
    const response = await Host_APIS.getCommunityMessages(queryParams);
    const { status = false, data = {} } = response || {};
    if (status) {
      const { totalPages } = data;
      const myUserId = getUserId();
      const { content = [] } = data;
      if (setFilters)
        setFilters((filters) => new FilterDTO({ ...filters, totalPages }));
      const dataToSet = content.map(
        (i) => new MessageDTO({ ...i, sentByMe: i.senderId === myUserId })
      );
      setData([...dataToSet.reverse(), ...messages]);
      // else {
      //     preventScrolling();
      // }
    }
  };

  const getLibraryResources = async (
    data: ResourceDTO[],
    setData: Function,
    queryParams: any,
    setFilters: Function,
    keepPreviousData: boolean = true
  ) => {
    const response = await Host_APIS.getLibraryResources(queryParams);
    const { status = false } = response || {};
    if (status) {
      const {
        pageNumber = 0,
        pageSize = 10,
        totalElements = 10,
        totalPages = 1,
        content = [],
      } = response?.data || {};

      const newData = content.map((i) => new ResourceDTO(i));

      if (keepPreviousData) {
        setData([...data, ...newData]);
      } else {
        setData(newData);
      }

      setFilters(
        (filters) =>
          new ResourceFilterDTO({
            ...filters,
            page: pageNumber,
            size: pageSize,
            totalElements,
            totalPages,
          })
      );
    }
  };

  const getLibraryResourceById = async (
    resourceId: number,
    setData: Function
  ) => {
    const response = await Host_APIS.getLibraryResourceById(resourceId);
    const { status = false, data = {} } = response || {};
    if (status) {
      const { content = [] } = data;
      setData(content.map((i) => new CommunityDTO(i)));
    }
  };

  const getReviewsByEventId = async (eventId: number, setData: Function) => {
    const response = await Host_APIS.getReviewsByEventId(eventId);
    const { status = false, data = {} } = response || {};
    if (status) {
      setData(data.content);
    }
  };

  const getCupsByEventId = async (eventId: number, setData: Function) => {
    const response = await Host_APIS.getCupsByEventId(eventId);
    const { status = false, data = {} } = response || {};
    if (status) {
      setData(data);
    }
  };

  const getEventAttendance = async (eventId: number, setData: Function) => {
    const response = await Host_APIS.getEventAttendance(eventId);
    const { status = false, data = {} } = response || {};
    if (status) {
      setData(data);
    }
  };

  const postReviewJoinee = async (body: PostJoineeDTO, eventId: number) => {
    const response = await Host_APIS.postReviewJoinee(eventId, body);
    const { status = false, message = "Success" } = response || {};
    if (status) {
      successToaster(message);
      navigate(-1);
    }
  };

  const getCoreConcept = async (body: any, setData: Function) => {
    const response = await Host_APIS.getCoreConcept(body);
    const { status = false, message = "", data = null } = response || {};
    if (status && data) {
      const { content = [] } = data;
      setData(content);
      // successToaster(message);
    }
  };

  const getHostBalance = async (setHostBalance: Function) => {
    const response = await Host_APIS.getHostBalance();
    const { status = false, data = {} } = response || {};
    if (status) {
      setHostBalance(data);
    }
  };


  const getHostEarnings = async (
    setHostEarnings: Function,
    queryParams: any = {},
    setTotalElements?: Function
  ) => {
    const response = await Host_APIS.getHostEarnings(queryParams);
    const { status = false, data = null } = response || {};
    if (status && data) {
      const { content = [], totalElements = 0 } = data;
      setHostEarnings(content);
      setTotalElements?.(totalElements);
    }
  };

  const postStripeConnect = async () => {
    const response = await Host_APIS.postStripeConnect();
    const { status = false, data = {} } = response || {};
    if (status && data.accountLink) {
      // Open Stripe Connect in a new tab
      window.open(data.accountLink, "_blank");
    }
  };

  const getStripeAccountStatus = async (
    setStripeAccountStatus: Function,
    setHostBalance?: Function
  ) => {
    const response = await Host_APIS.getStripeAccountStatus();
    const { status = false, data = {} } = response || {};
    if (status) {
      setStripeAccountStatus(data);
      // If this API updates hostBalance data, we should also update hostBalance state
      if (setHostBalance && data.hostBalance) {
        setHostBalance(data.hostBalance);
      }
    }
    return response;
  };

  const getHostEarningsOnboarding = async (
    setHostEarningsOnboarding: Function
  ) => {
    const response = await Host_APIS.getHostEarningsOnboarding();
    const { status = false, data = {} } = response || {};
    if (status) {
      setHostEarningsOnboarding(data);
    }
    return response;
  };

  const getStripDashboardLink = async (setStripDashboardLink: Function) => {
    const response = await Host_APIS.stripDashboardLink();
    const { status = false, data = {} } = response || {};
    if (status) {
      setStripDashboardLink(data);
    }
    return response;
  };

  const getHostRatedEvents = async (setData: Function) => {
    const response = await Host_APIS.getHostRatedEvents();
    const { status = false, data = [] } = response || {};
    if (status) {
      setData(data.map((event: any) => new EventDTO(event)));
    }
  };

  const getProfileData = async () => {
    const response = await Host_APIS.getProfileData();
    const { status = false, message = "" } = response || {};

    if (status) {
      return response.data;
    }
  };

  // Function to refresh and update registered data from API
  const refreshUserData = async () => {
    try {
      const profileData = await getProfileData();
      if (profileData) {
        // Update localStorage with fresh data from API
        updateRegisteredData(profileData);
        console.log("User data refreshed and localStorage updated!");
        return profileData;
      }
    } catch (error) {
      console.error("Failed to refresh user data:", error);
    }
  };

  const getCancelEventsTicketsListing = async (
    setData: Function,
    queryParams: any = {},
    setTotalElements?: Function
  ) => {
    const response = await Host_APIS.getCancelEventsTicketsListing(queryParams);
    const { status = false, data = null } = response || {};
    if (status && data) {
      const { content = [], totalElements = 0 } = data;
      setData(content);
      setTotalElements?.(totalElements);
    }
  };

  const getChatTicketMessages = async (
    id: number,
    setData: Function,
    params?: any
  ) => {
    try {
      const response = await Host_APIS.getChatTicketMessages(id, params);
      const { status = false, data = {} } = response || {};
      if (status) {
        setData(data);
      }
      return response;
    } catch (error) {
      console.error("Error fetching chat ticket messages:", error);
      return null;
    }
  };

  const postChatTicketMessages = async (id: number, body: any) => {
    try {
      const response = await Host_APIS.postChatTicketMessages(id, body);
      const { status = false, message = "" } = response || {};
      if (status) {
        // successToaster(message);
      }
      return response;
    } catch (error) {
      console.error("Error posting chat ticket message:", error);
      return null;
    }
  };

  const hostCancelSubscribtion = async (
    queryParams: any
    // setData: Function
  ) => {
    const response = await Host_APIS.hostCancelSubscribtion(queryParams);
    const { status = false } = response || {};
    if (status) {
      getUserProfile();
      // getSubscriptionPlans(setData);
    }
  };

  const hostResumeSubscribtion = async () => {
    const response = await Host_APIS.hostResumeSubscribtion();
    const { status = false } = response || {};
    if (status) {
      getUserProfile();
      // getSubscriptionPlans(setData);
    }
  };

  const toggleNotification = async () => {
    const response = await Host_APIS.toggleNotification();
    const { status = false, message = "" } = response || {};
    if (status) {
      successToaster(message);
    }
  };

  const getHostProfile = async (setHostProfileData: Function) => {
    const response = await Host_APIS.getHostProfileData();
    const { status = false, data } = response || {};
    if (status) {
      setHostProfileData({
        ...data.user,
        ...data.profiles.host,
      });
    }
  };

  return {
    getSubscriptionPlans,
    getCommunityMessages,
    getCommunities,
    sendMessageToCommunity,
    createCheckoutSession,
    getEvents,
    getEventById,
    deleteRsvp,
    getHostHome,
    getRsvps,
    getBadges,
    createEvent,
    createBadge,
    getAdminBadges,
    getAwardedBadges,
    cancelEvent,
    duplicateEvent,
    getLibraryResources,
    getLibraryResourceById,

    getReviewsByEventId,
    getCupsByEventId,
    getEventAttendance,
    postReviewJoinee,
    getCoreConcept,
    getHostBalance,
    getHostEarnings,
    postStripeConnect,
    getStripeAccountStatus,
    getHostEarningsOnboarding,
    getHostRatedEvents,
    getStripDashboardLink,
    refreshUserData,
    getCancelEventsTicketsListing,
    getChatTicketMessages,
    postChatTicketMessages,
    hostCancelSubscribtion,
    hostResumeSubscribtion,
    toggleNotification,
    getHostProfile,
  };
};

export default useHost;
